import Cookies from 'js-cookie';
import { Modal as bsModal } from 'bootstrap';
import { addMonths, subMonths, format, formatISO, isThisMonth, isSameDay } from 'date-fns';
import { watchViewport, unwatchViewport } from 'tornis';
import { breakPoint } from '../../js/config';

import './opening-times-calendar.scss';

// endpoint where month data comes from
const monthDataURL = window.calendarMonthEndpoint;
// endpoint where day detail comes from
const dayDetailDataURL = window.calendarDetailEndpoint;

// Memory cache of previously fetched dates
const detailsCache = new Map();

// Get from the cache, or make a new fetch request
async function getOtDetail(date, id) {
    let cacheID = `${id}--${date}`; // unique id for date and attraction
    if (!detailsCache.has(cacheID)) {
        let detailHtml = await (await window.fetch(dayDetailDataURL + '?date=' + date + '&attractionId=' + id)).text();
        detailsCache.set(cacheID, detailHtml);
    }
    return detailsCache.get(cacheID);
}
class OpeningTimesCalendar {

    constructor(el) {
        this.el = el;
        // get attraction id to use in queries for data
        this.attractionID = el.dataset.attractionId;
        // do we need to show the instrcutions modal?
        this.instructionsModal = el.querySelector('.instructions-modal');
        // store month listing container element
        this.monthContentWrapper = el.querySelector('ol.opening-times--month');
        // month switching stuff
        this.monthsControls = {
            dropdownToggle: el.querySelector('.opening-times--controls .dropdown .dropdown-toggle'),
            dropdownLinks: el.querySelectorAll('.opening-times--controls .dropdown .dropdown-menu a.dropdown-item'),
            prevBtn: el.querySelector('.opening-times--controls .prev-month'),
            nextBtn: el.querySelector('.opening-times--controls .next-month')
        }
        this.monthOptions = []; // store the data-date values of the dropdown options
        this.monthOptionIndex = 0
        this.monthsControls.dropdownLinks.forEach((l, i) => {
            this.monthOptions.push(new Date(l.dataset.date + 'T00:00:00.000Z'));
            if(l.classList.contains('active')) this.monthOptionIndex = i;
            // add behaviour
            l.onclick = (e) => {
                e.preventDefault();
                this.loadMonth(i);
            };
        });
        // update controls
        this.updateControls();
        // add btn controls behaviours
        this.monthsControls.prevBtn.onclick = (e) => {
            e.preventDefault();
            this.loadMonth(this.monthOptionIndex - 1);
        };
        this.monthsControls.nextBtn.onclick = (e) => {
            e.preventDefault();
            this.loadMonth(this.monthOptionIndex + 1);
        };
        // process the initial month content
        this.monthContent = new MonthContent(this.monthContentWrapper, this.attractionID);
        // check cookie to see if we need to show the instructions modal
        let modalName = 'otInstructionsModalShown';
        if(typeof Cookies.get(modalName) == 'undefined') {
            let m = bsModal.getOrCreateInstance(this.instructionsModal);
            m.show();
            // set cookie
            Cookies.set(modalName, 'true');
        }
    }

    updateControls() {
        // prev button
        if(this.monthOptionIndex > 0) {
            this.monthsControls.prevBtn.removeAttribute('disabled');
            this.monthsControls.prevBtn.classList.remove('disabled');
            let date = formatISO(subMonths(this.monthOptions[this.monthOptionIndex - 1], 1), { representation: 'date' });
            this.monthsControls.prevBtn.setAttribute('href', "?month=" + date + "&attractionId=" + this.attractionID);
            this.monthsControls.prevBtn.setAttribute('data-date', date);
        } else {
            this.monthsControls.prevBtn.classList.add('disabled');
            this.monthsControls.prevBtn.setAttribute('disabled','disabled');
        }
        // dropdown
        this.monthsControls.dropdownToggle.innerHTML = format(this.monthOptions[this.monthOptionIndex], 'MMMM y');
        this.monthsControls.dropdownLinks.forEach((l, i) => {
            l.classList.toggle('active', i == this.monthOptionIndex);
        });
        // next button
        if(this.monthOptionIndex < (this.monthOptions.length - 1)) {
            this.monthsControls.nextBtn.removeAttribute('disabled');
            this.monthsControls.nextBtn.classList.remove('disabled');
            let date = formatISO(subMonths(this.monthOptions[this.monthOptionIndex + 1], 1), { representation: 'date' });
            this.monthsControls.nextBtn.setAttribute('href', "?month=" + date + "&attractionId=" + this.attractionID);
            this.monthsControls.nextBtn.setAttribute('data-date', date);
        } else {
            this.monthsControls.nextBtn.classList.add('disabled');
            this.monthsControls.nextBtn.setAttribute('disabled','disabled');
        }
    }

    loadMonth(newMonthOptionIndex) {
        let _this = this;
        if(newMonthOptionIndex != this.monthOptionIndex) {
            // load
            let r = new XMLHttpRequest();
            r.open('GET', `${monthDataURL}?month=${this.monthOptions[newMonthOptionIndex].toISOString()}&attractionId=${this.attractionID}`, true);
            r.onreadystatechange = function () {
                if (r.readyState != 4 || r.status != 200) return;
                _this.monthContentWrapper.style.opacity = 0;
                // listen for end of opacity transition
                _this.monthContentWrapper.ontransitionend = (e) => {
                    if(e.target == _this.monthContentWrapper) {
                        _this.monthContentWrapper.ontransitionend = null;
                        _this.monthContentWrapper.innerHTML = r.responseText;
                        _this.monthContentWrapper.style.opacity = 1;
                        _this.monthOptionIndex = newMonthOptionIndex;
                        _this.updateControls()
                        // reset
                        this.monthContent = null;
                        // start over
                        this.monthContent = new MonthContent(_this.monthContentWrapper, _this.attractionID);
                    }
                }
            };
            r.send();
        }
    }

}

class MonthContent {
    constructor(el, attractionID) {
        this.el = el;
        this.attractionID = attractionID;
        // day detail element
        this.dayDetail = {
            el: el.querySelector('.day-detail'),
            expanded: false,
            wrapper: el.querySelector('.day-detail--wrapper'),
            inner: el.querySelector('.day-detail--content'),
            closeButton: el.querySelector('.day-detail--wrapper button.close')
        };
        // buttons
        this.buttons = [];
        this.buttonActiveIndex = -1;
        el.querySelectorAll('button[data-date]').forEach((b, i) => {
            let button = {
                el: b,
                index: i,
                date: b.dataset.date,
                expanded: false,
                order: b.parentElement.style.order
            };
            button.el.onclick = (e) => {
                if(button.expanded) {
                    this.closeDetails();
                    this.buttonActiveIndex = -1;
                } else {
                    if(this.dayDetail.expanded) {
                        // turn off old button
                        this.buttons[this.buttonActiveIndex].expanded = false;
                        this.buttons[this.buttonActiveIndex].el.setAttribute('aria-expanded', false);
                        // close existing then open new day detail
                        this.closeDetails(() => {this.openDetails(button)});
                    } else {
                        // open new day detail
                        this.openDetails(button);
                    }
                    button.expanded = true;
                    button.el.setAttribute('aria-expanded', true);
                    this.buttonActiveIndex = button.index;
                }
            };
            this.buttons.push(button);
        });
        // details close button
        this.dayDetail.closeButton.onclick = () => {
            this.closeDetails();
            this.buttons[this.buttonActiveIndex].el.focus();
        }
        // watch for viewport changes - update heights and/or order values
        this.watcherLastWidth = 0;
        this.watcher = ({size}) => {
            if (size.changed) {
                if (this.dayDetail.expanded) {
                    this.fitToContent();
                    // logical throttle, we only need to call again on a breakPoint change
                    if ((this.watcherLastWidth < breakPoint.MD && size.x >= breakPoint.MD)
                        || (this.watcherLastWidth >= breakPoint.MD && size.x < breakPoint.MD)) {
                        this.updateOrder(this.buttons[this.buttonActiveIndex]);
                    }
                }
                this.watcherLastWidth = size.x;
            }
        }
        watchViewport(this.watcher);
    }

    updateOrder(button) {
        if (button == null) {
            return;
        }
        let order = button.order;
        if (window.innerWidth >= breakPoint.MD) {
            order = (Math.ceil(order / 7) * 7);
        }
        this.dayDetail.el.style.order = order;
    }

    openDetails(button) {
        console.log('open details');
        this.updateOrder(button);
        getOtDetail(button.date, this.attractionID).then((content) => {
            this.dayDetail.inner.innerHTML = content;
            // expand area
            this.dayDetail.el.setAttribute('aria-expanded', true);
            this.dayDetail.el.ontransitionend = (e) => {
                if(e.target == this.dayDetail.el) {
                    // set focus on first link or button inside details element
                    this.dayDetail.inner.querySelector('a, button').focus();
                    this.dayDetail.el.ontransitionend = null;
                }
            }
            // animate in
            this.fitToContent();
            this.dayDetail.expanded = true;
        });
    }

    closeDetails(callback) {
        console.log('close details');
        this.dayDetail.expanded = false;
        this.dayDetail.el.setAttribute('aria-expanded', false);
        // animate out
        this.dayDetail.el.ontransitionend = (e) => {
            if(e.target == this.dayDetail.el && e.propertyName == 'max-height') {
                if (typeof callback === 'function') {
                    callback();
                    this.dayDetail.el.ontransitionend = null;
                } else {
                    this.dayDetail.el.style.order = '100';
                }
            }
        };
        this.dayDetail.el.style.maxHeight = 0;
        // turn off active button
        this.buttons[this.buttonActiveIndex].expanded = false;
        this.buttons[this.buttonActiveIndex].el.setAttribute('aria-expanded', false);
    }

    fitToContent() {
        if(this.dayDetail.el.style.maxHeight != this.dayDetail.wrapper.offsetHeight + 'px') this.dayDetail.el.style.maxHeight = this.dayDetail.wrapper.offsetHeight + 'px';
    }

}

// export the default function to create
export function createFrom(wrappers) {
    wrappers.forEach((node) => {
        // if node is an element
        if (node.nodeType === 1) {
            new OpeningTimesCalendar(node);
        }
    });
}